import { motion, AnimatePresence } from "framer-motion"
import { X } from "lucide-react"
import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { useSeletorDoTopo } from "@/hooks/useSeletorDoTopo"
import { useIsMobile } from "@/hooks/useMediaQuery"
import { cn } from "@/lib/utils"
import { GearUpgradeIcon } from "./GearUpgradeIcon"

interface SupportFABProps {
  onOpen: () => void
  isWidgetOpen: boolean
  hidden?: boolean
}

/**
 * Botão flutuante "mágico" no canto inferior direito.
 *
 * Comportamento:
 *   - Pulse animation quando idle
 *   - Ao abrir o widget, rotaciona 90° (vira X)
 *   - Esconde quando teclado mobile aberto (visualViewport encolhe)
 *   - Esconde via prop `hidden` (ex: quando sidebar mobile aberto)
 *   - Marcado com data-screenshot-hidden pra não aparecer no print
 */
export function SupportFAB({ onOpen, isWidgetOpen, hidden = false }: SupportFABProps) {
  // Mesma cor do seletor do topo (no Financeiro, a do escopo do módulo).
  const { cor: activeTenantColor } = useSeletorDoTopo()
  const isMobile = useIsMobile()
  const [keyboardOpen, setKeyboardOpen] = useState(false)

  // Detectar teclado mobile via visualViewport
  useEffect(() => {
    if (!isMobile || typeof window === "undefined") return
    const vv = window.visualViewport
    if (!vv) return

    const onResize = () => {
      const heightDiff = window.innerHeight - vv.height
      setKeyboardOpen(heightDiff > 100)
    }
    vv.addEventListener("resize", onResize)
    return () => vv.removeEventListener("resize", onResize)
  }, [isMobile])

  const shouldHide = hidden || keyboardOpen
  const accentColor = activeTenantColor ?? "#7027FA"
  const sizePx = isMobile ? 48 : 56

  if (typeof document === "undefined") return null

  // Renderiza via createPortal direto no body. ESSENCIAL: senão o FAB fica
  // preso no stacking context do AppLayout (`<div className="z-10">`) e
  // mesmo com zIndex alto, modais portal'd no body (z-50) ficam por cima.
  // Usando portal, FAB e modais ficam no mesmo stacking context (root),
  // então zIndex: 60 vence z-50 limpo.
  const fab = (
    <AnimatePresence>
      {!shouldHide && (
        <motion.button
          type="button"
          onClick={onOpen}
          aria-label={isWidgetOpen ? "Fechar feedback" : "Abrir feedback"}
          data-screenshot-hidden="true"
          className={cn(
            "fixed rounded-full flex items-center justify-center",
            "shadow-lg shadow-black/20",
            "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-offset-2",
            isMobile ? "bottom-4 right-4" : "bottom-6 right-6",
          )}
          style={{
            width: sizePx,
            height: sizePx,
            background: `linear-gradient(135deg, ${accentColor} 0%, ${shadeColor(accentColor, -15)} 100%)`,
            boxShadow: `0 8px 24px ${accentColor}55, 0 0 0 0 ${accentColor}55`,
            zIndex: 60,
          }}
          initial={{ scale: 0, rotate: -90, opacity: 0 }}
          animate={{
            scale: 1,
            rotate: isWidgetOpen ? 90 : 0,
            opacity: 1,
            boxShadow: [
              `0 8px 24px ${accentColor}55, 0 0 0 0 ${accentColor}66`,
              `0 8px 24px ${accentColor}55, 0 0 0 14px ${accentColor}00`,
              `0 8px 24px ${accentColor}55, 0 0 0 0 ${accentColor}66`,
            ],
          }}
          exit={{ scale: 0, opacity: 0, transition: { duration: 0.15 } }}
          transition={{
            scale: { type: "spring", stiffness: 400, damping: 25 },
            rotate: { type: "spring", stiffness: 300, damping: 20 },
            opacity: { duration: 0.2 },
            boxShadow: { duration: 3, repeat: Infinity, ease: "easeOut" },
          }}
          whileHover={{
            scale: 1.08,
            transition: { duration: 0.15 },
          }}
          whileTap={{ scale: 0.94 }}
        >
          <AnimatePresence mode="wait" initial={false}>
            {isWidgetOpen ? (
              <motion.span
                key="close"
                initial={{ opacity: 0, rotate: -90 }}
                animate={{ opacity: 1, rotate: 0 }}
                exit={{ opacity: 0, rotate: 90 }}
                transition={{ duration: 0.15 }}
                className="text-white"
              >
                <X className={isMobile ? "w-5 h-5" : "w-6 h-6"} />
              </motion.span>
            ) : (
              <motion.span
                key="gear-upgrade"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.15 }}
                className="text-white drop-shadow-sm"
              >
                <GearUpgradeIcon size={isMobile ? 24 : 28} />
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>
      )}
    </AnimatePresence>
  )

  return createPortal(fab, document.body)
}

/** Escurece/clareia uma cor hex em N% */
function shadeColor(hex: string, percent: number): string {
  const cleaned = hex.replace("#", "")
  if (cleaned.length !== 6) return hex
  const num = parseInt(cleaned, 16)
  const r = Math.max(0, Math.min(255, ((num >> 16) & 0xff) + Math.round((255 * percent) / 100)))
  const g = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + Math.round((255 * percent) / 100)))
  const b = Math.max(0, Math.min(255, (num & 0xff) + Math.round((255 * percent) / 100)))
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`
}
